import { styled } from "styled-components";
import { Typography } from "components/typography";
import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";
import {
  Container,
  TemplateArea,
  TemplateItemBg,
  TemplateItem,
} from "../../../../booking/calendar/selectTemplateButton/selectTemplateButton.styles";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { useOnClickOutside } from "hooks";

import { useSetIcbtRequestFeedbackDateIgnored } from "api/useIcbtQueries";
import { useFlag } from "@mindlercare/unleash-client";
import { Icon, useSnackbar } from "@mindlercare/ui-web";
import { IcbtTasks } from "api/types";

interface Props {
  tasks: IcbtTasks;
  displayDropdownUpwards?: boolean;
  isFeedbackLocked?: boolean;
  editFeedbackDateFn?: () => void;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  position: relative;
`;

// Dropdown area, positioned relative to the arrow icon
const IcbtFeedbackStateCellDropDownArea = styled(TemplateArea)`
  top: 20px;
  right: unset;
`;

const IcbtTaskTableBg = styled(TemplateItemBg)`
  white-space: nowrap;
`;

export const IcbtFeedbackTaskCell = ({
  tasks,
  displayDropdownUpwards,
  isFeedbackLocked,
  editFeedbackDateFn,
}: Props) => {
  const { t } = useTranslation();
  const [popupVisible, setShowPopup] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const feedbackPublished = !!tasks.feedbackPublishedAt;
  const editDueDate = t("EDIT_ICBT_FEEDBACK_DATE");
  const noFeedbackNeededText = t("NO_FEEDBACK_NEEDED");
  const feedbackSentAtLabel = feedbackPublished
    ? t("FEEDBACK_STATUS_SENT")
    : t("FEEDBACK_DRAFT_STATUS_SENT");

  const ignoreFeedbackDateFeatureEnabled = useFlag("NO_ICBT_FEEDBACK_NEEDED");

  const feedbackStateTextFromNewestFeedback = tasks.feedbackPublishedAt
    ? feedbackSentAtLabel
    : tasks.tasks.some((task) => task.type === "give_feedback")
      ? t("ICBT_TASK.GIVE_FEEDBACK")
      : "--";

  const feedbackStateText =
    ignoreFeedbackDateFeatureEnabled && tasks?.feedbackDateIgnored
      ? noFeedbackNeededText
      : feedbackStateTextFromNewestFeedback;

  const canEditFeedbackDate = tasks.tasks.some(
    (task) => task.type === "extend_due_date",
  );

  const canIgnoreFeedbackDate =
    !feedbackPublished && !tasks?.feedbackDateIgnored;

  const canCeaseIgnoreFeedbackDate =
    !feedbackPublished && !!tasks?.feedbackDateIgnored;

  const ceaseIgnoreFeedbackDateDropdownItemText = t("FEEDBACK_NEEDED");

  const menuItemsLength = [
    canIgnoreFeedbackDate,
    canCeaseIgnoreFeedbackDate,
    canEditFeedbackDate,
  ].filter((menuItem) => menuItem === true).length;

  const showArrowDown = ignoreFeedbackDateFeatureEnabled && menuItemsLength > 0;

  const noFeedbackNeeded =
    ignoreFeedbackDateFeatureEnabled &&
    feedbackStateText === noFeedbackNeededText;

  const feedbackStateTextColor = noFeedbackNeeded
    ? "ACTION_BLUE"
    : "GRAY_BLACK";

  const { mutateAsync: toggleFeedbackDateIgnored } =
    useSetIcbtRequestFeedbackDateIgnored();

  const arrowDownRef = useRef(null);
  const popupRef = useRef(null);

  useOnClickOutside(popupRef, popupVisible, () => setShowPopup(false), [
    arrowDownRef,
  ]);

  const feedbackSeenAt = tasks.feedbackPatientSeenAt;
  const getFeedbackIcon = () => {
    if (noFeedbackNeeded)
      return (
        <Icon.NoFeedback
          width={defaultTheme.ICON_SIZE.STANDARD}
          color={defaultTheme.COLORS.GRAY}
        />
      );
    if (feedbackPublished && feedbackSeenAt)
      return (
        <Icon.FeedbackCheckmark
          width={defaultTheme.ICON_SIZE.STANDARD}
          color={defaultTheme.COLORS.SUCCESS}
        />
      );
    if (feedbackPublished)
      return (
        <Icon.FeedbackCheckmark
          width={defaultTheme.ICON_SIZE.STANDARD}
          color={defaultTheme.COLORS.ERROR}
        />
      );
    return (
      <Icon.Feedback
        width={defaultTheme.ICON_SIZE.STANDARD}
        color={defaultTheme.COLORS.GRAY}
      />
    );
  };

  return (
    <Wrapper>
      <IconFeedbackRow>
        <IconColumnWrapper>
          {tasks.assessmentStatus.status === "AWAITING_ASSESSMENT" ? (
            <Icon.Feedback
              width={defaultTheme.ICON_SIZE.STANDARD}
              color={defaultTheme.COLORS.ACTION_BLUE}
            />
          ) : tasks.assessmentStatus.status === "UNAVAILABLE" ? (
            tasks.assessmentStatus.reason === "PATIENT_NOT_ACTIVE" ? (
              <Icon.Clock
                width={defaultTheme.ICON_SIZE.STANDARD}
                color={defaultTheme.COLORS.ACTION_BLUE}
              />
            ) : (
              <Icon.NoFeedback
                width={defaultTheme.ICON_SIZE.STANDARD}
                color={defaultTheme.COLORS.GRAY_BLACK}
              />
            )
          ) : (
            <Icon.FeedbackCheckmark
              width={defaultTheme.ICON_SIZE.STANDARD}
              color={defaultTheme.COLORS.SUCCESS}
            />
          )}
        </IconColumnWrapper>
        <FeedbackTextColumn>
          <Typography insensitive variant="body-small">
            {t(`ASSESSMENT_STATUS_SHORT.${tasks.assessmentStatus.status}`)}
          </Typography>
        </FeedbackTextColumn>
      </IconFeedbackRow>
      {feedbackPublished ? (
        // === Mode: Published Feedback
        <IconFeedbackRow>
          <IconColumnWrapper>{getFeedbackIcon()}</IconColumnWrapper>
          <FeedbackTextColumn>
            <Typography
              insensitive
              variant="body-small"
              color={feedbackStateTextColor}
            >
              {feedbackStateText}
            </Typography>
            <Typography insensitive variant="body-small" color="GRAY_DARK">
              {feedbackSeenAt
                ? t("TABLE_STATUS_READ")
                : t("TABLE_STATUS_NOT_READ")}
            </Typography>
          </FeedbackTextColumn>
        </IconFeedbackRow>
      ) : (
        // === Mode: Interactive Dropdown
        <IconFeedbackRow>
          {!isFeedbackLocked && getFeedbackIcon()}
          <Typography
            insensitive
            variant="body-small"
            color={feedbackStateTextColor}
            underline={feedbackStateTextColor === "ACTION_BLUE"}
          >
            {feedbackStateText}
          </Typography>

          {showArrowDown && (
            <div ref={arrowDownRef}>
              <Icon.Expand
                fill={defaultTheme.COLORS.LIGHT_GREY}
                onClick={(event) => {
                  event.stopPropagation();
                  setShowPopup((prev) => !prev);
                }}
              />
            </div>
          )}
          {popupVisible && (
            <Container
              ref={popupRef}
              style={{
                position: "absolute",
                left: 0,
                top: displayDropdownUpwards ? -60 : 0,
              }}
            >
              <IcbtFeedbackStateCellDropDownArea>
                {canIgnoreFeedbackDate && (
                  <IcbtTaskTableBg>
                    <TemplateItem
                      onClick={(event: React.MouseEvent) => {
                        event.stopPropagation();
                        toggleFeedbackDateIgnored({
                          mode: "IGNORE",
                          icbtRequestId: tasks.icbtRequestId,
                        })
                          // mutateAsync already throws on error
                          .then(() => {
                            enqueueSnackbar({
                              text: t("IGNORED_ICBT_FEEDBACK_DATE"),
                              isError: false,
                            });
                            setShowPopup(false);
                          })
                          .catch(() => {
                            enqueueSnackbar({
                              text: t("FAILED_SAVE"),
                              isError: true,
                            });
                          });
                      }}
                    >
                      <Typography
                        insensitive
                        variant="body-x-small-bold"
                        color="GRAY_BLACK"
                      >
                        {noFeedbackNeededText}
                      </Typography>
                    </TemplateItem>
                  </IcbtTaskTableBg>
                )}
                {canCeaseIgnoreFeedbackDate && (
                  <IcbtTaskTableBg>
                    <TemplateItem
                      onClick={(event: React.MouseEvent) => {
                        event.stopPropagation();
                        toggleFeedbackDateIgnored({
                          mode: "CEASE_IGNORE",
                          icbtRequestId: tasks.icbtRequestId,
                        })
                          // mutateAsync already throws on error
                          .then(() => {
                            enqueueSnackbar({
                              text: t("CEASE_IGNORE_ICBT_FEEDBACK_DATE"),
                              isError: false,
                            });
                            setShowPopup(false);
                          })
                          .catch(() =>
                            enqueueSnackbar({
                              text: t("FAILED_SAVE"),
                              isError: true,
                            }),
                          );
                      }}
                    >
                      <Typography
                        insensitive
                        variant="body-x-small-bold"
                        color="GRAY_BLACK"
                      >
                        {ceaseIgnoreFeedbackDateDropdownItemText}
                      </Typography>
                    </TemplateItem>
                  </IcbtTaskTableBg>
                )}
                {canEditFeedbackDate &&
                  typeof editFeedbackDateFn === "function" && (
                    <IcbtTaskTableBg>
                      <TemplateItem
                        onClick={(event: React.MouseEvent) => {
                          event.stopPropagation();
                          event.preventDefault();
                          editFeedbackDateFn();
                        }}
                      >
                        <Typography
                          insensitive
                          variant="body-x-small-bold"
                          color="GRAY_BLACK"
                        >
                          {editDueDate}
                        </Typography>
                      </TemplateItem>
                    </IcbtTaskTableBg>
                  )}
              </IcbtFeedbackStateCellDropDownArea>
            </Container>
          )}
        </IconFeedbackRow>
      )}
    </Wrapper>
  );
};

const IconFeedbackRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.SPACING.SM};
`;

const FeedbackTextColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const IconColumnWrapper = styled.div`
  //margin-top: 14px;
`;
